import { USER_ROLES } from "@kansai-ai-hub/types";
import { z } from "zod";
export const loginSchema = z.object({
    email: z.string().email("有効なメールアドレスを入力してください"),
    password: z.string().min(1, "パスワードを入力してください"),
});
/**
 * パスワード強度ポリシー。新規発行(admin.ts createUserSchema)・自己変更・管理者リセットの
 * 全経路で共有する（セキュリティレビュー指摘: 旧6文字は弱すぎたため10文字に引き上げ）。
 */
export const passwordSchema = z
    .string()
    .min(10, "パスワードは10文字以上で入力してください")
    // 上限が無いと、未認証の申込フォーム経由で極端に長い入力を argon2 に流し込める。
    // 1GiB のコンテナで memoryCost 19MiB × 同時数を積まれると効いてくるため歯止めを置く。
    .max(128, "パスワードは128文字以内で入力してください")
    // regex は 1 本のまま、エラー文だけ入力に応じて切り替える。固定文だと全角文字や空白で
    // 弾かれても「英字と数字を…」と出てしまう。regex を 2 本に分けると OpenAPI の pattern が
    // allOf になり、生成クライアントの型が string から unknown に崩れるため分けない。
    .regex(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d!-/:-@[-`{-~]+$/, {
    error: (issue) => typeof issue.input === "string" && !/^[A-Za-z\d!-/:-@[-`{-~]+$/.test(issue.input)
        ? "半角英数字・記号のみで入力してください（空白や全角文字は使えません）"
        : "英字と数字を両方含めてください",
});
/**
 * パスワード入力欄の下に出すチェックリスト用の条件別判定。
 * `passwordSchema` を条件ごとに分解したもので、入力中に「どれを満たしたか」を示すために使う。
 * 全て true なら（128 文字上限を除き）`passwordSchema` も通る（auth.spec.ts で担保）。
 */
export function checkPasswordRules(password) {
    return {
        minLength: password.length >= 10,
        hasLetterAndDigit: /[A-Za-z]/.test(password) && /\d/.test(password),
        // 空欄では「満たした」扱いにしない（未入力で緑が点くのを防ぐ）。
        allowedChars: /^[A-Za-z\d!-/:-@[-`{-~]+$/.test(password),
    };
}
/** 自己パスワード変更（ログイン中の全ロール共通）。 */
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, "現在のパスワードを入力してください"),
    newPassword: passwordSchema,
});
/** 管理者によるパスワード強制リセット（ADMIN専用）。 */
export const adminResetPasswordSchema = z.object({
    newPassword: passwordSchema,
});
// ── ユーザー自身によるパスワード再設定（ENTERPRISE / MEMBER） ────────────────
// メールのワンタイムリンク方式。3 エンドポイント（request / verify / confirm）とも
// 未認証で叩けるため、入力の形だけをここで縛り、存在判定は一切返さない
// （Docs/task/ユーザー側のパスワード再発行機能/01_設計方針.md §4 ユーザー列挙対策）。
/** 再設定メールの送信リクエスト。登録の有無に関わらず応答は常に同じ。 */
export const passwordResetRequestSchema = z.object({
    email: z.string().email("有効なメールアドレスを入力してください"),
});
/** リンクを開いた直後の生死確認。トークンは消費しない。 */
export const passwordResetVerifySchema = z.object({
    token: z.string().min(1),
});
/**
 * 新パスワードの確定（トークンを消費する）。
 * `newPassword` は新規発行・自己変更・管理者リセットと同じ `passwordSchema` を共有する
 * ——— リセット経路だけ弱いポリシーになると、そこが最短の突破口になる。
 */
export const passwordResetConfirmSchema = z.object({
    token: z.string().min(1),
    newPassword: passwordSchema,
});
export const authUserSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string(),
    role: z.enum(USER_ROLES),
    organizationId: z.string(),
    organizationName: z.string(),
    organizationImageUrl: z.string().nullable().optional(),
    organizationDesc: z.string().nullable().optional(),
    homepageUrl: z.string().nullable().optional(),
    department: z.string().nullable().optional(),
    contactRole: z.string().nullable().optional(),
});
